import { autocompletion, Completion, CompletionContext, CompletionResult, snippet } from '@codemirror/autocomplete';
import { Diagnostic, linter } from '@codemirror/lint';
import { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { FormulaArgument, FormulaCatalog, FormulaMeta } from '@shared/interfaces/lsp.interface';

/** Provides the flow variables offered after `{{`. */
export type VariableProvider = () => string[];

/**
 * Resolves a lint message i18n key (+ MessageFormat params) to display text.
 * Passed in (rather than injecting TranslateService) so this stays a pure,
 * DI-free util — the component supplies `(k, p) => translate.instant(k, p)`.
 */
export type FormulaTranslate = (key: string, params?: Record<string, unknown>) => string;

const VARIABLE_TRIGGER = /\{\{[\w.]*$/;
const FORMULA_TRIGGER = /::\w*$/;
const PREVIOUS_FORMULA = /::(\w+)\([^)]*\)\s*$/;

function signatureLabel(meta: FormulaMeta): string {
  const args = (meta.arguments ?? [])
    .map((argument) => (argument.optional ? `${argument.name}?` : argument.name))
    .join(', ');

  return `${meta.name}(${args})`;
}

function argumentSnippet(meta: FormulaMeta): string {
  const required = (meta.arguments ?? []).filter((argument) => !argument.optional);
  const placeholders = required.map((argument) => `\${${argument.name}}`).join(', ');

  return `${meta.name}(${placeholders})`;
}

/** Single styled inline span — keeps the docs builders declarative. */
function span(className: string, text: string): HTMLSpanElement {
  const el = document.createElement('span');

  el.className = className;
  el.textContent = text;

  return el;
}

function argumentName(argument: FormulaArgument): string {
  return argument.optional ? `${argument.name}?` : argument.name;
}

// `name(arg, arg?) → returns`, with each part coloured. Built from `meta`
// (not parsed) so the colouring is always correct.
function signatureLine(meta: FormulaMeta): HTMLElement {
  const line = document.createElement('div');

  line.classList.add('formula-docs-signature');
  line.appendChild(span('formula-docs-sig-name', meta.name));
  line.appendChild(span('formula-docs-sig-punct', '('));

  (meta.arguments ?? []).forEach((argument, index) => {
    if (index > 0) {
      line.appendChild(span('formula-docs-sig-punct', ', '));
    }

    line.appendChild(span('formula-docs-sig-param', argumentName(argument)));
  });

  line.appendChild(span('formula-docs-sig-punct', ')'));
  line.appendChild(span('formula-docs-sig-arrow', ' → '));
  line.appendChild(span('formula-docs-sig-return', meta.returns));

  return line;
}

// Light, parser-free tokenizer — enough to give the example block an IDE feel
// without a real expression parser. Classifies `{{ }}` tokens, quoted strings,
// numbers, `name(` function calls, and `:: ( ) ,` punctuation; anything else
// is appended as plain text in the base colour.
function appendHighlightedExample(host: HTMLElement, code: string): void {
  const pattern = /\{\{[^}]*\}\}|'[^']*'|"[^"]*"|\d+(?:\.\d+)?|[A-Za-z_]\w*|::|[(),]|\s+|[^\s]/g;
  const tokens = code.match(pattern) ?? [code];

  tokens.forEach((token, index) => {
    let className: string | null = null;

    if (token.startsWith('{{')) {
      className = 'formula-docs-ex-token';
    } else if (token.startsWith("'") || token.startsWith('"')) {
      className = 'formula-docs-ex-string';
    } else if (/^\d/.test(token)) {
      className = 'formula-docs-ex-number';
    } else if (token === '::' || token === '(' || token === ')' || token === ',') {
      className = 'formula-docs-ex-punct';
    } else if (/^[A-Za-z_]\w*$/.test(token) && tokens[index + 1] === '(') {
      className = 'formula-docs-ex-name';
    }

    host.appendChild(className ? span(className, token) : document.createTextNode(token));
  });
}

function renderDocs(meta: FormulaMeta): HTMLElement {
  const container = document.createElement('div');

  container.classList.add('formula-docs');

  // Signature line (top, with a divider underneath).
  container.appendChild(signatureLine(meta));

  // Description prose.
  const summary = document.createElement('div');

  summary.classList.add('formula-docs-summary');
  summary.textContent = meta.summary;
  container.appendChild(summary);

  // `@param name type — description` lines.
  for (const argument of meta.arguments ?? []) {
    const line = document.createElement('div');

    line.classList.add('formula-docs-argument');
    line.appendChild(span('formula-docs-arg-kw', '@param'));
    line.appendChild(document.createTextNode(' '));
    line.appendChild(span('formula-docs-arg-name', argumentName(argument)));
    line.appendChild(document.createTextNode(' '));
    line.appendChild(span('formula-docs-arg-type', argument.type));

    if (argument.description) {
      line.appendChild(span('formula-docs-arg-desc', ` — ${argument.description}`));
    }

    container.appendChild(line);
  }

  // Dark, syntax-highlighted example block (with eyebrow label).
  if (meta.example) {
    const label = document.createElement('div');

    label.classList.add('formula-docs-example-label');
    label.textContent = 'Example';
    container.appendChild(label);

    const example = document.createElement('code');

    example.classList.add('formula-docs-example');
    appendHighlightedExample(example, meta.example);
    container.appendChild(example);
  }

  return container;
}

/** Type the previous chain step produces, so the next step can be ranked by it. */
function previousReturnType(textBefore: string, catalog: FormulaCatalog): string | null {
  const match = textBefore.match(PREVIOUS_FORMULA);

  if (!match) {
    return null;
  }

  const previous = catalog[match[1]];

  return previous ? previous.returns : null;
}

function accepts(meta: FormulaMeta, inputType: string | null): boolean {
  if (!inputType || meta.accepts.includes('any')) {
    return true;
  }

  return meta.accepts.includes(inputType as FormulaMeta['returns']);
}

function formulaOption(meta: FormulaMeta): Completion {
  return {
    label: meta.name,
    type: 'function',
    detail: `${meta.accepts.join('|')} → ${meta.returns}`,
    info: () => renderDocs(meta),
    apply: snippet(argumentSnippet(meta)),
  };
}

export function formulaCompletionSource(catalog: FormulaCatalog, getVariables: VariableProvider) {
  return (context: CompletionContext): CompletionResult | null => {
    const variableMatch = context.matchBefore(VARIABLE_TRIGGER);

    if (variableMatch) {
      return {
        from: variableMatch.from + 2,
        options: getVariables().map((name) => ({ label: name, type: 'variable' })),
      };
    }

    const formulaMatch = context.matchBefore(FORMULA_TRIGGER);

    if (formulaMatch) {
      const textBefore = context.state.sliceDoc(0, formulaMatch.from);
      const inputType = previousReturnType(textBefore, catalog);
      const formulas = Object.values(catalog);
      const matching = formulas.filter((meta) => accepts(meta, inputType));
      const rest = formulas.filter((meta) => !accepts(meta, inputType));

      return {
        from: formulaMatch.from + 2,
        options: [...matching, ...rest].map(formulaOption),
      };
    }

    return null;
  };
}

function requiredArgumentCount(meta: FormulaMeta): number {
  return (meta.arguments ?? []).filter((argument: FormulaArgument) => !argument.optional).length;
}

/** Collects diagnostics for a document: misplaced `::` in variables, unknown formulas, too-few args. */
export function collectFormulaDiagnostics(
  catalog: FormulaCatalog,
  text: string,
  translate: FormulaTranslate,
): Diagnostic[] {
  // Local /g regexes — `.exec` in a loop is stateful via `lastIndex`, so they
  // must not be shared across calls.
  const variableBlockPattern = /\{\{([^}]*)\}\}/g;
  // `::name` with an OPTIONAL `(args)` — the BE accepts paren-less calls
  // (zero-arg formulas may omit `()`), so missing parens is an arity question,
  // never a syntax error.
  const callPattern = /::(\w+)(?:\(([^)]*)\))?/g;
  const diagnostics: Diagnostic[] = [];
  const variableRanges: Array<[number, number]> = [];
  let match: RegExpExecArray | null;

  // Variable interpolations: `{{ name }}` must not contain formula syntax —
  // `::` belongs after the closing braces (`{{x}}::upper()`), not inside them.
  // (The BE silently fails the lookup; flagging it catches the mistake early.)
  while ((match = variableBlockPattern.exec(text)) !== null) {
    const [block, inner] = match;
    const from = match.index;
    const to = from + block.length;

    variableRanges.push([from, to]);

    if (inner.includes('::')) {
      diagnostics.push({
        from,
        to,
        severity: 'error',
        message: translate('flow.formula.lint.colon-in-variable'),
      });
    }
  }

  while ((match = callPattern.exec(text)) !== null) {
    const [call, name, rawArguments] = match;
    const from = match.index;

    // A `::` sitting inside a `{{ }}` block is already reported above — don't
    // double-flag it as a (paren-less) formula call.
    if (variableRanges.some(([start, end]) => from >= start && from < end)) {
      continue;
    }

    const to = from + call.length;
    const meta = catalog[name];

    if (!meta) {
      // A paren-less unknown name is most likely a half-typed formula — stay
      // quiet until it's actually called with `(` (or completed to a known
      // name). Only a parenthesised call commits to "this is the formula".
      if (rawArguments === undefined) {
        continue;
      }

      diagnostics.push({ from, to, severity: 'error', message: translate('flow.formula.lint.unknown', { name }) });
      continue;
    }

    // No parentheses → zero arguments (still a valid call shape per the BE).
    const provided = rawArguments && rawArguments.trim() !== '' ? rawArguments.split(',').length : 0;
    const required = requiredArgumentCount(meta);

    if (provided < required) {
      diagnostics.push({
        from,
        to,
        severity: 'error',
        message: translate('flow.formula.lint.too-few-args', {
          name,
          count: required,
          signature: signatureLabel(meta),
        }),
      });
    }
  }

  return diagnostics;
}

/** Flags unknown formula names and calls with too few required arguments. */
export function formulaLinter(catalog: FormulaCatalog, translate: FormulaTranslate): Extension {
  return linter((view: EditorView): Diagnostic[] =>
    collectFormulaDiagnostics(catalog, view.state.doc.toString(), translate),
  );
}

// NOTE: the completion tooltip + docs-panel styling lives in global SCSS
// (src/styles/components/_flow-ui.scss, keyed on `.cm-tooltip-autocomplete` /
// `.cm-completionInfo` / `.formula-docs-*`). CodeMirror injects the tooltip
// into a body-level overlay, so global CSS — matching the repo's existing
// pattern for overlay-injected DOM — reaches it reliably. renderDocs() above
// only supplies the class hooks those rules target.

/** CodeMirror extensions giving the formula field completions, signature help, and lint. */
export function buildFormulaIntelligence(
  catalog: FormulaCatalog,
  getVariables: VariableProvider,
  translate: FormulaTranslate,
): Extension[] {
  return [
    autocompletion({ override: [formulaCompletionSource(catalog, getVariables)] }),
    formulaLinter(catalog, translate),
  ];
}
